import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const API_BASE_URL = (() => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  if (import.meta.env.DEV) {
    // Em DEV, sem VITE_API_URL explícita, use o próprio origin (Vite) + /api/v1
    // para funcionar via proxy tanto localmente quanto dentro do Docker.
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/api/v1`;
    }
    return "/api/v1";
  }

  return "https://api-librattum.sinapsecorp.com.br/api/v1";
})();

/**
 * Resolve a URL de mídia servida pelo backend.
 *
 * Arquivos enviados (Materiais/Vídeo-aulas) são servidos estaticamente pelo
 * backend em `/uploads` (raiz do backend, FORA do prefixo `/api/v1`). Assets
 * relativos do próprio app (ex.: `/materiais/…`) e URLs absolutas passam
 * inalterados.
 */
export const resolveMediaUrl = (path?: string | null): string => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  // /uploads/... = arquivo estático; /api/... = rota assinada (vídeo-aula).
  // Ambos moram na origem do backend, não na do app.
  if (path.startsWith("/uploads/") || path.startsWith("/api/")) {
    const origin = API_BASE_URL.replace(/\/api\/v\d+\/?$/, "");
    return `${origin}${path}`;
  }
  return path;
};

export interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  needs_2fa?: boolean;
}

// Chaves da sessão. "Manter Conectado" (Remember Me): em cada aba os tokens
// vivem em exatamente UM storage — localStorage quando o usuário opta por manter
// a sessão persistente, sessionStorage (some ao fechar o navegador) caso contrário.
const TOKEN_KEYS = ["access_token", "refresh_token", "token_expires_at", "last_activity"] as const;

// Wrappers defensivos: storage pode falhar (modo privado / quota).
const safeGet = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (storage: Storage, key: string, value: string): void => {
  try {
    storage.setItem(key, value);
  } catch {
    // storage indisponível: degrada sem persistir, sem quebrar o fluxo.
  }
};

const safeRemove = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key);
  } catch {
    // noop
  }
};

// Storage ativo da aba (session-first). null quando não há sessão.
const getActiveStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  if (safeGet(window.sessionStorage, "access_token")) {
    return window.sessionStorage;
  }
  if (safeGet(window.localStorage, "access_token")) {
    return window.localStorage;
  }
  return null;
};

// Leitura session-first, com fallback para local.
const readToken = (key: string): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return safeGet(window.sessionStorage, key) ?? safeGet(window.localStorage, key);
};

export const getAccessToken = () => readToken("access_token");
export const getRefreshToken = () => readToken("refresh_token");

/**
 * Sessão persistente ("Manter Conectado") = tokens no localStorage.
 * Definido pelo storage ativo (session-first) para não confundir uma aba efêmera
 * com um token remember-me remanescente no localStorage compartilhado entre abas.
 */
export const isPersistentSession = (): boolean =>
  typeof window !== "undefined" && getActiveStorage() === window.localStorage;

/**
 * Persiste os tokens.
 * @param remember quando boolean, escolhe o storage (localStorage se true) e limpa
 *   AMBOS antes de gravar (evita tokens órfãos). Quando omitido (caminho de
 *   refresh), preserva o storage ativo da aba.
 */
export const setTokens = (tokens: TokenResponse, remember?: boolean) => {
  if (typeof window === "undefined") {
    return;
  }

  let target: Storage;
  if (typeof remember === "boolean") {
    // Login / 2FA / impersonação: destino explícito, elimina órfãos do outro storage.
    for (const key of TOKEN_KEYS) {
      safeRemove(window.localStorage, key);
      safeRemove(window.sessionStorage, key);
    }
    target = remember ? window.localStorage : window.sessionStorage;
  } else {
    // Refresh: mantém o storage atual da sessão.
    target = getActiveStorage() ?? window.sessionStorage;
  }

  if (tokens.access_token) {
    safeSet(target, "access_token", tokens.access_token);
  }
  if (tokens.refresh_token) {
    safeSet(target, "refresh_token", tokens.refresh_token);
  }
  // Store token expiry time
  if (tokens.expires_in) {
    const expiresAt = Date.now() + tokens.expires_in * 1000;
    safeSet(target, "token_expires_at", expiresAt.toString());
  }
};

export const clearTokens = () => {
  if (typeof window === "undefined") {
    return;
  }
  for (const key of TOKEN_KEYS) {
    safeRemove(window.localStorage, key);
    safeRemove(window.sessionStorage, key);
  }
};

export const clearSubAccountToken = () => {
  if (typeof window === "undefined") {
    return;
  }
  safeRemove(window.localStorage, "subaccount_access_token");
  safeRemove(window.sessionStorage, "subaccount_access_token");
};

// Atividade e expiração operam no storage ativo da aba (usados pelo hook de
// inatividade e pelo contexto de autenticação).
export const touchActivity = () => {
  const storage = getActiveStorage();
  if (storage) {
    safeSet(storage, "last_activity", Date.now().toString());
  }
};

export const getLastActivity = () => readToken("last_activity");

export const getTokenExpiry = () => readToken("token_expires_at");

export const setTokenExpiry = (expiresAtMs: number) => {
  const storage = getActiveStorage();
  if (storage) {
    safeSet(storage, "token_expires_at", expiresAtMs.toString());
  }
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Attempt to refresh the access token.
 */
async function tryRefreshToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    // Use axios directly to avoid interceptors
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: refreshToken,
    });

    const tokens = response.data;
    setTokens({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token || refreshToken,
      token_type: tokens.token_type,
      expires_in: tokens.expires_in,
    });

    return tokens.access_token;
  } catch {
    return null;
  }
}

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Modo de manutenção: respostas 503 com `maintenance_mode` levam o app
    // imediatamente à tela de manutenção (o gate também faz polling do status).
    const detail = (error?.response?.data as { detail?: { maintenance_mode?: boolean; message?: string } } | undefined)?.detail;
    if (error?.response?.status === 503 && detail?.maintenance_mode) {
      window.dispatchEvent(
        new CustomEvent('app:maintenance', { detail: { message: detail.message } }),
      );
      throw error;
    }

    // Only handle 401 errors
    if (error?.response?.status !== 401) {
      throw error;
    }

    // Don't retry if we've already tried or if this is the refresh endpoint
    if (originalRequest._retry || originalRequest.url?.includes('/auth/refresh')) {
      clearTokens();
      clearSubAccountToken();

      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      throw error;
    }

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise<string | null>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (token && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        })
        .catch((err) => {
          throw err;
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await tryRefreshToken();

      if (newToken) {
        processQueue(null, newToken);
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        
        return api(originalRequest);
      } else {
        // Refresh failed - clear tokens and redirect to login
        processQueue(error, null);
        clearTokens();
        clearSubAccountToken();

        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        throw error;
      }
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearTokens();
      clearSubAccountToken();

      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
